import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import useGetAdminBusinessInsightsDashboard from '@/hooks/admin/dashboard/useGetAdminBusinessInsightsDashboard';
import useGetAdminBusinessInsightsGrowth from '@/hooks/admin/dashboard/useGetAdminBusinessInsightsGrowth';
import useGetAdminBusinessInsightsOverview from '@/hooks/admin/dashboard/useGetAdminBusinessInsightsOverview';
import DashboardTabs from './DashboardTabs';
import getDashboardTabItems from './helpers/getDashboardTabItems';
import getDashboardTabPath from './helpers/getDashboardTabPath';
import resolveDashboardPeriodLabel from './helpers/resolveDashboardPeriodLabel';
import resolveDashboardTab from './helpers/resolveDashboardTab';
import { DashboardContainer, DataSourceBadge, ScrollBody, ScrollPeriod, TabBar } from './styled';
import type { DashboardOutletContext } from './types';

const AdminBusinessInsightsDashboard = () => {
    const { dashboard } = useGetAdminBusinessInsightsDashboard();
    const location = useLocation();
    const navigate = useNavigate();
    const activeTabId = resolveDashboardTab({ pathname: location.pathname });
    const isSummaryTab = activeTabId === 'summary';
    const isGrowthTab = activeTabId === 'growth';
    const isLiveTab = isSummaryTab || isGrowthTab;

    const {
        overview,
        isLoadingOverview,
        error: overviewError,
    } = useGetAdminBusinessInsightsOverview({
        enabled: isSummaryTab,
    });
    const {
        growth,
        isLoadingGrowth,
        error: growthError,
    } = useGetAdminBusinessInsightsGrowth({
        enabled: isGrowthTab,
    });

    const outletContext: DashboardOutletContext = {
        dashboard,
    };

    const periodLabel = resolveDashboardPeriodLabel({
        activeTabId,
        overviewPeriodLabel: overview?.periodLabel ?? null,
        isLoadingOverview,
        hasOverviewError: Boolean(overviewError),
        growthPeriodLabel: growth?.periodLabel ?? null,
        isLoadingGrowth,
        hasGrowthError: Boolean(growthError),
        mockPeriodLabel: dashboard.periodLabel,
    });

    return (
        <DashboardContainer>
            <TabBar>
                <DashboardTabs
                    items={getDashboardTabItems()}
                    activeTabId={activeTabId}
                    onSelectTab={({ tabId }) => {
                        navigate(
                            getDashboardTabPath({
                                tabId,
                                search: location.search,
                            })
                        );
                    }}
                >
                    <ScrollPeriod>{periodLabel}</ScrollPeriod>
                    <DataSourceBadge $variant={isLiveTab ? 'live' : 'mock'} role="status">
                        {isLiveTab ? 'Live' : 'Mock data'}
                    </DataSourceBadge>
                </DashboardTabs>
            </TabBar>
            <ScrollBody>
                <Outlet context={outletContext} />
            </ScrollBody>
        </DashboardContainer>
    );
};

export default AdminBusinessInsightsDashboard;
